import React, { useState } from 'react';
import { Clock, Bell, Calendar, AlertTriangle, CheckCircle, TrendingUp } from 'lucide-react';

export default function RenewalTracking() {
  const [selectedTimeframe, setSelectedTimeframe] = useState('30');

  const renewalItems = [
    {
      id: 1,
      document: 'Factory License',
      category: 'Operational Licenses',
      expiryDate: '2025-01-20',
      daysUntilExpiry: 15,
      status: 'critical',
      renewalInitiated: false,
      lastRenewal: '2024-01-20',
      renewalCycle: 'Annual',
      responsiblePerson: 'John Manager',
      notificationsSent: 3
    },
    {
      id: 2,
      document: 'Environmental Clearance',
      category: 'Environmental Permits',
      expiryDate: '2025-02-15',
      daysUntilExpiry: 41,
      status: 'warning',
      renewalInitiated: true,
      lastRenewal: '2024-02-15',
      renewalCycle: 'Annual',
      responsiblePerson: 'Sarah Admin',
      notificationsSent: 2
    },
    {
      id: 3,
      document: 'Hazardous Waste Authorization',
      category: 'Environmental Permits',
      expiryDate: '2025-06-30',
      daysUntilExpiry: 177,
      status: 'upcoming',
      renewalInitiated: false,
      lastRenewal: '2023-06-30',
      renewalCycle: 'Biennial',
      responsiblePerson: 'Mike Safety',
      notificationsSent: 1
    },
    {
      id: 4,
      document: 'Fire Safety Certificate',
      category: 'Safety Certifications',
      expiryDate: '2026-03-10',
      daysUntilExpiry: 460,
      status: 'ok',
      renewalInitiated: false,
      lastRenewal: '2024-03-10',
      renewalCycle: 'Biennial',
      responsiblePerson: 'John Manager',
      notificationsSent: 0
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'warning':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'upcoming':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'ok':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'critical':
        return <AlertTriangle className="text-red-600" size={20} />;
      case 'warning':
        return <Clock className="text-orange-600" size={20} />;
      case 'upcoming':
        return <Bell className="text-yellow-600" size={20} />;
      case 'ok':
        return <CheckCircle className="text-green-600" size={20} />;
      default:
        return null;
    }
  };

  const filteredItems = renewalItems?.filter(item => {
    const days = parseInt(selectedTimeframe);
    return item?.daysUntilExpiry <= days;
  });

  const stats = {
    critical: renewalItems?.filter(i => i?.status === 'critical')?.length,
    warning: renewalItems?.filter(i => i?.status === 'warning')?.length,
    upcoming: renewalItems?.filter(i => i?.status === 'upcoming')?.length,
    total: renewalItems?.length
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-red-800">Critical</p>
            <AlertTriangle className="text-red-600" size={20} />
          </div>
          <p className="text-2xl font-bold text-red-900">{stats?.critical}</p>
          <p className="text-xs text-red-700 mt-1">Expires within 30 days</p>
        </div>
        
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-orange-800">Warning</p>
            <Clock className="text-orange-600" size={20} />
          </div>
          <p className="text-2xl font-bold text-orange-900">{stats?.warning}</p>
          <p className="text-xs text-orange-700 mt-1">Expires within 60 days</p>
        </div>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-yellow-800">Upcoming</p>
            <Bell className="text-yellow-600" size={20} />
          </div>
          <p className="text-2xl font-bold text-yellow-900">{stats?.upcoming}</p>
          <p className="text-xs text-yellow-700 mt-1">Expires within 180 days</p>
        </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-blue-800">Total Tracked</p>
            <TrendingUp className="text-blue-600" size={20} />
          </div>
          <p className="text-2xl font-bold text-blue-900">{stats?.total}</p>
          <p className="text-xs text-blue-700 mt-1">All documents</p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Show expiring within:</label>
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e?.target?.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="30">30 days</option>
            <option value="60">60 days</option>
            <option value="90">90 days</option>
            <option value="180">180 days</option>
            <option value="365">1 year</option>
          </select>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
          <Calendar size={20} />
          Schedule Renewals
        </button>
      </div>

      {/* Renewal Items */}
      <div className="space-y-4">
        {filteredItems?.map((item) => (
          <div
            key={item?.id}
            className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start gap-4 flex-1">
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  {getStatusIcon(item?.status)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-gray-900">{item?.document}</h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded border ${getStatusColor(item?.status)}`}>
                      {item?.status?.charAt(0)?.toUpperCase() + item?.status?.slice(1)}
                    </span>
                    {item?.renewalInitiated && (
                      <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200 rounded">
                        Renewal in Progress
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{item?.category}</p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600 mb-1">Expires In</p>
                      <p className="font-medium text-gray-900">{item?.daysUntilExpiry} days</p>
                    </div>
                    <div>
                      <p className="text-gray-600 mb-1">Expiry Date</p>
                      <p className="font-medium text-gray-900">{item?.expiryDate}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 mb-1">Renewal Cycle</p>
                      <p className="font-medium text-gray-900">{item?.renewalCycle}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 mb-1">Responsible Person</p>
                      <p className="font-medium text-gray-900">{item?.responsiblePerson}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                <span>Time until expiry</span>
                <span>{item?.daysUntilExpiry} days remaining</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    item?.status === 'critical' ? 'bg-red-600' :
                    item?.status === 'warning' ? 'bg-orange-600' :
                    item?.status === 'upcoming'? 'bg-yellow-600' : 'bg-green-600'
                  }`}
                  style={{ width: `${Math.max(10, (item?.daysUntilExpiry / 365) * 100)}%` }}
                />
              </div>
            </div>

            {/* Notification Info */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Bell size={16} />
                <span>{item?.notificationsSent} notification{item?.notificationsSent !== 1 ? 's' : ''} sent</span>
              </div>
              <div className="flex gap-2">
                {!item?.renewalInitiated && (
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm">
                    Initiate Renewal
                  </button>
                )}
                <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm">
                  View Details
                </button>
              </div>
            </div>
          </div>
        ))}

        {filteredItems?.length === 0 && (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <CheckCircle className="mx-auto text-green-600 mb-4" size={48} />
            <h3 className="text-lg font-medium text-gray-900 mb-2">All Clear!</h3>
            <p className="text-gray-600">No documents expiring within the selected timeframe</p>
          </div>
        )}
      </div>

      {/* Automated Notification Settings */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Bell className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <h4 className="font-medium text-blue-900 mb-2">Automated Renewal Notifications</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-blue-800">
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-blue-600" />
                <span>90 days before expiry</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-blue-600" />
                <span>60 days before expiry</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-blue-600" />
                <span>30 days before expiry</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}